// EQUIPOS + FICHA que Solar Hub pide a FluxAI para el monitoreo.
//
// Contrato: docs/consumo-suite.md. Solar Hub NO embebe la consola AMI.
// Pide el recurso `equipos` del espejo y pinta fichas. Si el puente no
// responde, se listan los equipos ya ligados a cotizaciones — no se inventa flota.

import { pedirAFlux, reiniciarCanalFlux } from './flux';

const cache = new Map();
const TTL_MS = 60 * 1000;

function texto(v, max) {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t ? t.slice(0, max) : null;
}

/** Allowlist de la ficha. Lo que no está acá no se muestra (ni se inventa). */
export function fichaDeEquipo(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const id = raw.device_id != null ? String(raw.device_id).trim() : '';
  if (!id) return null;
  return {
    device_id: id.slice(0, 32),
    alias: texto(raw.alias, 80),
    marca: texto(raw.marca, 80),
    modelo: texto(raw.modelo || raw.modelo_medidor, 80),
    estado: texto(raw.estado, 32),
    en_linea: raw.en_linea === true,
    visto_en: texto(String(raw.visto_en || raw.vistoEn || ''), 40),
    protocolo: texto(raw.protocolo, 32),
    tipo_conexion: texto(raw.tipo_conexion, 32),
    tipo_medicion: texto(raw.tipo_medicion, 32),
    kwp: Number.isFinite(Number(raw.kwp)) ? Number(raw.kwp) : null,
    origen: texto(raw.origen, 24) || 'fluxai',
  };
}

/** Equipos que Solar Hub ya conoce por una cotización (sin pedir la suite). */
export function equiposDesdeCotizaciones(quotes, email) {
  const mail = String(email || '').toLowerCase().trim();
  const out = [];
  const seen = new Set();
  for (const q of Array.isArray(quotes) ? quotes : []) {
    if (mail && q.email && String(q.email).toLowerCase().trim() !== mail) continue;
    const id = q.flux_installation_id || q.payload?.flux_installation_id;
    if (!id || seen.has(String(id))) continue;
    seen.add(String(id));
    const kwp = Number(q.results?.actKwp);
    out.push(fichaDeEquipo({
      device_id: id,
      alias: q.systemType ? `Sistema ${q.systemType}` : 'Equipo de la cotización',
      marca: q.results?.panelBrand || q.panel?.brand || null,
      modelo: q.results?.panelModel || q.panel?.model || null,
      estado: q.status || null,
      kwp: Number.isFinite(kwp) ? kwp : null,
      origen: 'cotizacion',
    }));
  }
  return out.filter(Boolean);
}

/**
 * Lista equipos + ficha para el monitoreo nativo.
 * 1) espejo FluxAI `equipos`
 * 2) cotizaciones con `flux_installation_id`
 *
 * 🔴 DEVUELVE CUATRO ORÍGENES, Y LA DISTINCIÓN ES EL PUNTO. Antes eran tres, y `vacio` cargaba
 * dos significados OPUESTOS: «este correo no tiene equipos» y «no se pudo preguntar». La
 * pantalla elegía el primero y afirmaba «Aún no hay equipos asignados a este correo» — un hecho
 * sobre la cuenta del titular que nadie había medido.
 *
 * Medido en producción el 2026-08-21: el espejo responde **502** porque `internal/suite/equipos`
 * todavía no está desplegado en FluxAI, y con eso la pantalla le decía al titular que no tenía
 * equipos. `pedirAFlux` ya distingue los dos casos —devuelve el cuerpo crudo cuando alguien
 * contestó y `null` cuando ningún espejo sirvió—; lo que faltaba era no tirar esa señal acá.
 *
 *   · `fluxai`        → FluxAI contestó y entregó flota.
 *   · `cotizacion`    → sin flota del espejo, se listan los equipos ligados a cotizaciones.
 *   · `vacio`         → FluxAI CONTESTÓ y este correo no tiene equipos. Es un hecho.
 *   · `no_disponible` → NO se pudo preguntar. NO dice nada sobre la cuenta del titular.
 */
export async function listarEquiposMonitoreo({ email, quotes } = {}) {
  const mail = String(email || '').toLowerCase().trim();
  const key = mail || '_sesion';
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < TTL_MS) return hit.valor;

  let origen = 'vacio';
  let equipos = [];
  let motivo = null;
  // Sin este flag, «contestó que no hay» y «no contestó» terminaban en el mismo `equipos: []`.
  let sePudoPreguntar = false;

  if (mail) {
    const r = await pedirAFlux('equipos', { email: mail });
    if (r && r.encontrado && Array.isArray(r.equipos)) {
      equipos = r.equipos.map(fichaDeEquipo).filter(Boolean);
      origen = 'fluxai';
      sePudoPreguntar = true;
    } else if (r && r.ok !== false) {
      // Respuesta legítima de FluxAI: miró y no hay equipos para ese correo.
      sePudoPreguntar = true;
    } else {
      // `null` (ningún espejo) o un cuerpo con `ok:false` (el espejo existe, FluxAI falló).
      motivo = texto(r && r.motivo, 200) || 'el puente de equipos no respondió';
    }
  }

  if (!equipos.length) {
    equipos = equiposDesdeCotizaciones(quotes, mail);
    if (equipos.length) origen = 'cotizacion';
    else if (mail && !sePudoPreguntar) origen = 'no_disponible';
  }

  const valor = origen === 'no_disponible' ? { origen, equipos, motivo } : { origen, equipos };
  // Un fallo NO se cachea: cachearlo deja la pantalla afirmando lo mismo durante todo el TTL,
  // aunque FluxAI despliegue la capacidad en ese intervalo.
  if (origen !== 'no_disponible') cache.set(key, { at: Date.now(), valor });
  return valor;
}

export function limpiarCacheEquiposFlux() {
  cache.clear();
  reiniciarCanalFlux();
}
